import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { API_URL } from '../services/api';
import { trackEvent } from '../utils/analytics';

interface Vendor {
  id: number;
  name: string;
  contact_name: string | null;
  website: string | null;
  city: string;
  state: string;
  specialty: string;
  description: string | null;
  union_affiliated: boolean;
  is_featured: boolean;
  is_claimed: boolean;
  is_domain_confirmed?: boolean;
  slug: string | null;
  active_job_count: number;
}

const SPECIALTIES: { value: string; label: string }[] = [
  { value: '', label: 'All Specialties' },
  { value: 'union_labor', label: 'Union Labor' },
  { value: 'av_rental', label: 'AV Rental' },
  { value: 'audio', label: 'Audio' },
  { value: 'lighting', label: 'Lighting' },
  { value: 'video', label: 'Video' },
  { value: 'staging', label: 'Staging' },
  { value: 'backline', label: 'Backline' },
  { value: 'production', label: 'Production' },
  { value: 'general', label: 'General' },
];

const getSupportedSpecialty = (searchParams: URLSearchParams) => {
  const requestedSpecialty = searchParams.get('specialty') || '';
  return SPECIALTIES.some(({ value }) => value === requestedSpecialty)
    ? requestedSpecialty
    : '';
};

const SPECIALTY_LABELS: Record<string, string> = {
  union_labor: 'Union Labor',
  av_rental: 'AV Rental',
  audio: 'Audio',
  lighting: 'Lighting',
  video: 'Video',
  staging: 'Staging',
  backline: 'Backline',
  production: 'Production',
  general: 'General',
};

const SPECIALTY_COLORS: Record<string, string> = {
  union_labor: 'bg-blue-100 text-blue-800',
  av_rental: 'bg-purple-100 text-purple-800',
  audio: 'bg-green-100 text-green-800',
  lighting: 'bg-yellow-100 text-yellow-800',
  video: 'bg-red-100 text-red-800',
  staging: 'bg-orange-100 text-orange-800',
  backline: 'bg-pink-100 text-pink-800',
  production: 'bg-indigo-100 text-indigo-800',
  general: 'bg-gray-100 text-gray-700',
};

const getVendorPath = (vendor: Pick<Vendor, 'id' | 'slug'>) => `/vendors/${vendor.slug || vendor.id}`;

const getClaimHref = (vendor: Pick<Vendor, 'id' | 'slug'>) => {
  const redirect = `${getVendorPath(vendor)}?claim=1`;
  return `/register?${new URLSearchParams({ role: 'employer', redirect }).toString()}`;
};

export default function VendorsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const querySearch = searchParams.get('search') || searchParams.get('city') || '';
  const initialSearch = querySearch;
  const isClaimIntent = searchParams.get('claim') === '1';
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const specialty = getSupportedSpecialty(searchParams);
  const [searchInput, setSearchInput] = useState(initialSearch);

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '50', offset: '0' });
      if (searchTerm) params.set('search', searchTerm);
      if (specialty) params.set('specialty', specialty);

      const res = await fetch(`${API_URL}/api/vendors?${params}`);
      const data = await res.json();
      setVendors(data.vendors || []);
      setTotal(data.total || 0);
    } catch {
      setVendors([]);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, specialty]);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  useEffect(() => {
    setSearchTerm(querySearch);
    setSearchInput(querySearch);
  }, [querySearch]);

  const handleDirectorySearch = (e: React.FormEvent) => {
    e.preventDefault();
    const nextSearch = searchInput.trim();
    const nextParams = new URLSearchParams(searchParams);
    if (nextSearch) nextParams.set('search', nextSearch);
    else nextParams.delete('search');
    nextParams.delete('city');
    setSearchParams(nextParams, { replace: true });
    setSearchTerm(nextSearch);
    setSearchInput(nextSearch);
  };

  const clearDirectorySearch = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('search');
    nextParams.delete('city');
    setSearchParams(nextParams, { replace: true });
    setSearchTerm('');
    setSearchInput('');
  };

  const handleSpecialtyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const nextParams = new URLSearchParams(searchParams);
    const nextSpecialty = e.target.value;
    if (nextSpecialty) nextParams.set('specialty', nextSpecialty);
    else nextParams.delete('specialty');
    setSearchParams(nextParams);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title="Vendor Directory — Union Labor, AV Rental & Event Vendors | StageCrew"
        description="Browse live event vendor listings across the U.S. — union labor providers, AV rental houses, backline, staging, and more. Search by city and specialty on StageCrew."
        url="https://stagecrew.app/vendors"
        keywords="event vendor directory, union labor, AV rental, live event vendors, stagehand companies, backline rental, staging companies"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: 'StageCrew Vendor Directory',
          description: 'Live event vendor listings on StageCrew',
          url: 'https://stagecrew.app/vendors',
        }}
      />

      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-10">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {isClaimIntent ? 'Find and claim your company page' : 'Vendor Directory'}
          </h1>
          <p className="text-gray-500 text-lg">
            {isClaimIntent
              ? 'Search by company name, city, or state, then use the claim button on your listing.'
              : 'Browse live-event vendor listings — union labor, AV rental, backline, staging, and more. Claimed listings are marked.'}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-gray-100 shadow-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex flex-wrap gap-3 items-center">
          <form onSubmit={handleDirectorySearch} className="flex gap-2">
            <input
              type="text"
              placeholder="Company, city, or state..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 w-56"
            />
            <button
              type="submit"
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 transition"
            >
              Search
            </button>
            {searchTerm && (
              <button
                type="button"
                onClick={clearDirectorySearch}
                className="text-gray-500 text-sm hover:text-gray-700 px-2"
              >
                Clear
              </button>
            )}
          </form>

          <select
            value={specialty}
            onChange={handleSpecialtyChange}
            aria-label="Filter vendors by specialty"
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {SPECIALTIES.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>

          {!loading && (
            <span className="text-sm text-gray-400 ml-auto">
              {total} {total === 1 ? 'vendor' : 'vendors'}
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
                <div className="h-5 bg-gray-200 rounded w-3/4 mb-3" />
                <div className="h-4 bg-gray-100 rounded w-1/2 mb-4" />
                <div className="h-3 bg-gray-100 rounded w-full mb-2" />
                <div className="h-3 bg-gray-100 rounded w-2/3" />
              </div>
            ))}
          </div>
        ) : vendors.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <p className="text-lg font-medium">No vendors found</p>
            <p className="text-sm mt-1">Try clearing the filters or checking back later.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {vendors.map(vendor => (
              <article
                key={vendor.id}
                className={`bg-white rounded-xl border p-6 flex flex-col gap-3 hover:shadow-md transition ${
                  vendor.is_featured ? 'border-indigo-300 ring-1 ring-indigo-200' : 'border-gray-200'
                }`}
              >
                {/* Badges */}
                <div className="flex flex-wrap gap-2">
                  {vendor.active_job_count > 0 && (
                    <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                      Pay-listed jobs
                    </span>
                  )}
                  {vendor.is_featured && (
                    <span className="text-xs font-semibold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">
                      Featured Listing
                    </span>
                  )}
                  {vendor.is_claimed ? (
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      vendor.is_domain_confirmed
                        ? 'text-green-700 bg-green-50'
                        : 'text-gray-600 bg-gray-100'
                    }`}>
                      {vendor.is_domain_confirmed
                        ? 'Business domain confirmed'
                        : 'Managed listing'}
                    </span>
                  ) : (
                    <span className="text-xs font-semibold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                      Unclaimed
                    </span>
                  )}
                </div>

                {/* Name + specialty */}
                <div>
                  <Link
                    to={getVendorPath(vendor)}
                    className="text-lg font-semibold text-gray-900 hover:text-indigo-600 transition"
                  >
                    {vendor.name}
                  </Link>
                  <p className="text-sm text-gray-500">{vendor.city}, {vendor.state}</p>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${SPECIALTY_COLORS[vendor.specialty] || SPECIALTY_COLORS.general}`}>
                    {SPECIALTY_LABELS[vendor.specialty] || vendor.specialty}
                  </span>
                  {vendor.union_affiliated && (
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
                      Union
                    </span>
                  )}
                </div>

                {/* Description */}
                {vendor.description && (
                  <p className="text-sm text-gray-600 line-clamp-3">{vendor.description}</p>
                )}

                <div className="mt-auto pt-2 flex flex-wrap gap-2">
                  {!vendor.is_claimed && (
                    <Link
                      to={getClaimHref(vendor)}
                      onClick={() => trackEvent('claim_cta_click', 'vendor_claim', 'directory_card')}
                      className="inline-flex items-center justify-center rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 transition"
                    >
                      Claim this page
                    </Link>
                  )}
                  <Link
                    to={getVendorPath(vendor)}
                    className="inline-flex items-center justify-center rounded-lg border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 hover:border-indigo-300 hover:text-indigo-700 transition"
                  >
                    View profile
                  </Link>
                  {vendor.website && (
                    <a
                      href={vendor.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center rounded-lg border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 hover:border-indigo-300 hover:text-indigo-700 transition"
                    >
                      Website
                    </a>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}

        {/* CTA — become a partner */}
        <div className="mt-16 bg-indigo-50 border border-indigo-100 rounded-2xl p-8 text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Are you a vendor or partner?</h2>
          <p className="text-gray-500 mb-4">
            Request a StageCrew directory listing so event producers can review your service details.
          </p>
          <a
            href="mailto:edwin@stagecrew.app?subject=Vendor Directory Listing"
            className="inline-block bg-indigo-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-indigo-700 transition"
          >
            Request a Listing
          </a>
        </div>
      </div>
    </div>
  );
}
